package com.hyperether.goodjob.scenes.user

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Assignees
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.UserStatus
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.WebRepositoryImpl
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.admin
import com.hyperether.goodjob.resources.employee
import com.hyperether.goodjob.resources.manager
import com.hyperether.goodjob.resources.team_added
import com.hyperether.goodjob.resources.team_deleted
import com.hyperether.goodjob.resources.unexpected_error
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.resources.user_deleted
import com.hyperether.goodjob.resources.user_status_active
import com.hyperether.goodjob.resources.user_status_inactive
import com.hyperether.goodjob.resources.user_status_pending
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.lighthousegames.logging.logging

class UserViewModel(
    val repository: Repository
) : ViewModel() {

    val log = logging("EmployeesViewModel")

    val isDetailsView: MutableState<Boolean> = mutableStateOf(true)
    val showPopUpSuccess: MutableState<Boolean> = mutableStateOf(false)
    val successText: MutableState<String> = mutableStateOf("")
    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val errorText: MutableState<String> = mutableStateOf("")
    val showPopUpDelete: MutableState<Boolean> = mutableStateOf(false)
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val showFilterDialog: MutableState<Boolean> = mutableStateOf(false)

    /*filter states*/
    val selectedRoles = mutableStateOf(setOf<String>())
    var selectedCities = mutableStateOf(setOf<String>())
    var selectedTeams = mutableStateOf(setOf<String>())
    var selectedSkills = mutableStateOf(setOf<String>())
    var selectedStatuses = mutableStateOf(setOf<String>())

    val selectedUsers = mutableStateListOf<User>()

    private val _users = MutableStateFlow<List<User>>(emptyList())
    val users: StateFlow<List<User>> = _users

    private val _filteredUsers = MutableStateFlow<List<User>>(emptyList())
    val filteredUsers: StateFlow<List<User>> = _filteredUsers

    private val _teams = MutableStateFlow<List<Team>>(emptyList())
    val teams: StateFlow<List<Team>> = _teams

    var roleMap: Map<String, String> = mutableMapOf()
    var userStatusMap: Map<String, String> = mutableMapOf()

    var jobSearchText = mutableStateOf("")
    var jobSortBy: MutableState<String?> = mutableStateOf(null)

    var userRoles: Map<String, StringResource> = mapOf(
        Role.admin.name to Res.string.admin,
        Role.manager.name to Res.string.manager,
        Role.employee.name to Res.string.employee,
    )

    var userStatues: Map<String, StringResource> = mapOf(
        UserStatus.active.name to Res.string.user_status_active,
        UserStatus.inactive.name to Res.string.user_status_inactive,
        UserStatus.pending.name to Res.string.user_status_pending
    )

    init {
        if (getPlatformChecker().isMobile()) {
            init()
            loadRemoteData()
        }
    }

    fun init() {
        clearFilterValues()
        // In mobile, this will load DB content:
        // In web this will get API content
        viewModelScope.launch {
            repository.getUsersFlow().collect {
                _users.value = it
                _filteredUsers.value = filter(it)
            }
        }

        viewModelScope.launch {
            repository.getTeamsFlow().collect {
                _teams.value = it
            }
        }
    }

    fun loadRemoteData() {
        // refresh database with API content:
        repository.getToken().takeIf { it.isNotEmpty() }?.let {
            viewModelScope.launch {
                repository.getTeams()
                repository.getUsers()
                repository.getSkillsByWorkspace()
            }
        }
    }

    private fun deleteSelectedUsers(
        userList: List<User>,
        onAllDeleted: () -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch {
            try {
                userList.forEachIndexed { index, user ->
                    val isLastUser = index == userList.size - 1
                    user._id?.let { id ->
                        val result = repository.deleteUser(id)
                        if (result is Resource.Success && isLastUser) {
                            if (repository is WebRepositoryImpl) {
                                _users.value = repository.getUsers().data ?: emptyList()
                                _filteredUsers.value = _users.value
                            }
                            showPopUpSuccess.value = true
                            successText.value = stringResourcePlain(Res.string.user_deleted)
                            onAllDeleted()
                        } else if (result is Resource.Error) {
                            errorText.value =
                                result.text ?: stringResourcePlain(Res.string.unknown_error)
                            onError(id)
                            return@launch
                        }
                    }
                }
            } catch (e: Exception) {
                onError(stringResourcePlain(Res.string.unexpected_error) + " ${e.message}")
            }
        }
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun deleteSelectedTeams(
        selectedTeams: List<Team>,
        onAllDeleted: () -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch {
            try {
                selectedTeams.forEachIndexed { index, team ->
                    val isLastTeam = index == selectedTeams.size - 1
                    team._id?.let { id ->
                        val result = repository.deleteTeamById(id)
                        if (result is Resource.Success) {
                            if (repository is WebRepositoryImpl)
                                _teams.value = _teams.value.filter { it._id != id }
                            val filteredUsers = users.value.filter { user ->
                                user.teams?.any { team -> team?.id == id } == true
                            }
                            val updatedUsers = filteredUsers.map { user ->
                                user.copy(teams = user.teams?.filter { team -> team?.id != id })
                            }
                            updatedUsers.forEach { user ->
                                user._id?.let { userId ->
                                    val userUpdated =
                                        repository.updateUser(userId, Mapper().toUserRequest(user))
                                    if (userUpdated is Resource.Success) {
                                        userUpdated.data?.let { updatedUser ->
                                            _users.value = _users.value.map { existingUser ->
                                                if (existingUser._id == updatedUser._id) updatedUser else existingUser
                                            }
                                        }
                                    }
                                }
                            }

                            val jobs = repository.getAllJobsForWorkspace().data ?: emptyList()
                            val updatedJobs = jobs.filter { job ->
                                job.assignees?.teams?.any { team -> team?._id == id } == true
                            }.map { job ->
                                val newEmployees = job.assignees?.employees?.map { it?.copy() }
                                val newTeams =
                                    job.assignees?.teams?.filter { team -> team?._id != id }
                                        ?.map { it?.copy() }
                                val newAssignees = Assignees(
                                    employees = newEmployees,
                                    teams = newTeams
                                )
                                job.copy(assignees = newAssignees)
                            }
                            updatedJobs.forEach { job ->
                                job.id?.let { jobId ->
                                    repository.updateJob(jobId, Mapper().toJobUpdateRequest(job))
                                }
                            }
                        } else if (result is Resource.Error) {
                            errorText.value =
                                result.text ?: stringResourcePlain(Res.string.unknown_error)
                            showLoader.value = false
                            onError(id)
                            return@launch
                        }
                    }
                }
                if (repository is WebRepositoryImpl) {
                    _users.value = repository.getUsers().data ?: emptyList()
                    _filteredUsers.value = _users.value
                }

                showLoader.value = false
                onAllDeleted()
                showPopUpSuccess.value = true
                successText.value = stringResourcePlain(Res.string.team_deleted)
            } catch (e: Exception) {
                showLoader.value = false
                onError(stringResourcePlain(Res.string.unexpected_error) + " ${e.message}")
            }
        }
    }

    fun deleteUser(id: String, onDeleted: () -> Unit) {
        viewModelScope.launch {
            showLoader.value = true
            when (val result = repository.deleteUser(id)) {
                is Resource.Success -> {
                    showPopUpSuccess.value = true
                    successText.value = stringResourcePlain(Res.string.user_deleted)
                    showLoader.value = false
                    if (repository is WebRepositoryImpl) {
                        _users.value = repository.getUsers().data ?: emptyList()
                        _filteredUsers.value = _users.value
                    }
                    println("User deleted successfully")
                    delay(100)
                    onDeleted()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showPopUpError.value = true
                    showLoader.value = false
                    println("Error while deleting employee")
                }
            }
        }
    }

    fun createTeam(name: String) {
        viewModelScope.launch {
            when (val result = repository.createTeam(name)) {
                is Resource.Success -> {
                    result.data?.let { team ->
                        if (repository is WebRepositoryImpl)
                            _teams.value = _teams.value.toMutableList().apply { add(team) }

                        showPopUpSuccess.value = true
                        successText.value = stringResourcePlain(Res.string.team_added)
                    }
                    println(result.data)
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showPopUpError.value = true
                }
            }
        }
    }

    fun filter(userList: List<User>): List<User> {
        selectedUsers.clear()
        if (selectedCities.value.isEmpty() &&
            selectedStatuses.value.isEmpty() &&
            selectedRoles.value.isEmpty() &&
            selectedSkills.value.isEmpty() &&
            selectedTeams.value.isEmpty()
        ) {
            selectedUsers.addAll(userList.toSet())
        } else {
//            log.d("USER") { "${userList.forEach { it.status }}" }
            selectedUsers.addAll(userList.filter { user ->
                selectedCities.value.any { query ->
                    user.city?.equals(query, ignoreCase = true) == true
                }.or(selectedCities.value.isEmpty()) &&
                        selectedRoles.value.any { query ->
                            val mappedRole = roleMap.entries.find { entry ->
                                entry.value == user.role
                            }?.key
                            mappedRole?.equals(query, ignoreCase = true) == true
                        }.or(selectedRoles.value.isEmpty()) &&
                        selectedStatuses.value.any { query ->
                            val mappedStatus = when (user.confirmStatus) {
                                UserStatus.pending.name -> userStatusMap.entries.find { entry -> entry.value == user.confirmStatus }?.key
                                UserStatus.active.name -> userStatusMap.entries.find { entry -> entry.value == user.status }?.key
                                else -> null
                            }
                            mappedStatus?.equals(query, ignoreCase = true) == true
                        }.or(selectedStatuses.value.isEmpty()) &&
                        selectedTeams.value.any { query ->
                            user.teams?.any {
                                it?.value?.equals(
                                    query,
                                    ignoreCase = true
                                ) == true
                            } == true
                        }.or(selectedTeams.value.isEmpty()) &&
                        selectedSkills.value.any { query ->
                            user.skills?.any { it.equals(query, ignoreCase = true) } == true
                        }.or(selectedSkills.value.isEmpty())
            })
        }

        return selectedUsers
    }

    fun clearFilterValues() {
        selectedStatuses.value = setOf()
        selectedTeams.value = setOf()
        selectedSkills.value = setOf()
        selectedRoles.value = setOf()
        selectedCities.value = setOf()
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun onDeleteSelectedUsers(
        usersSelected: List<User>,
        onAllDeleted: () -> Unit,
    ) {
        if (usersSelected.isNotEmpty()) {
            showLoader.value = true
            deleteSelectedUsers(
                userList = usersSelected,
                onAllDeleted = {
                    viewModelScope.launch {
                        showPopUpSuccess.value = true
                        successText.value = stringResourcePlain(Res.string.user_deleted)
                        showLoader.value = false
                        onAllDeleted()
                    }
                },
                onError = {
                    showPopUpError.value = true
                    showLoader.value = false
                })
        }
    }

    fun onApplyFilter(
        cities: Set<String>,
        roles: Set<String>,
        teams: Set<String>,
        statuses: Set<String>,
        skills: Set<String>
    ) {
        selectedCities.value = cities
        selectedRoles.value = roles
        selectedStatuses.value = statuses
        selectedTeams.value = teams
        selectedSkills.value = skills

        val filteredList = filter(_users.value)
        _filteredUsers.value = filteredList
    }

    fun getSortedUsers(searchQuery: String): List<User> {
        val filteredUsersSearch = if (searchQuery.isNotBlank()) {
            _filteredUsers.value.filter { user ->
                user.getName().contains(
                    searchQuery,
                    ignoreCase = true
                )
            }
        } else {
            _filteredUsers.value
        }
        val sortedList = filteredUsersSearch.sortedBy { user ->
            (user.fullName ?: user.email)?.lowercase()
        }

        return sortedList
    }

    fun isInFilterMode(): Boolean {
        return selectedCities.value.isNotEmpty() ||
               selectedStatuses.value.isNotEmpty() ||
               selectedRoles.value.isNotEmpty() ||
               selectedSkills.value.isNotEmpty() ||
               selectedTeams.value.isNotEmpty()
    }
}